import { flexRender } from '@tanstack/react-table';
import React, { useCallback } from 'react';

import { RowProps } from '../../../../interfaces';
import CustomExpander from '../../../Expander';
import { useTabsViewsContext } from '../../../TabsViews';
import Cell from './Cell';
import { CellContainer, RowItem } from './styles';

const Row: React.FC<RowProps> = ({
  row,
  restItem,
  rowHeight,
  cellBorderColor,
  cellPadding,
  heatColorStart,
  heatColorEnd,
  expandableRows,
  dashMenuOptions,
  textMargin,
  justifyBadge,
  expanderBackgroundColor,
  newItems,
  onExport,
  parentExpanded,
  level,
  subgroupColor,
  deltaReversed,
  contentInfo,
  style,
  virtualRow,
  dataIndex,
  ref,
  closeRows,
}) => {
  const {
    hasTabs,
    tabsViews,
    activeTabs,
    tabsViewsMap,
  } = useTabsViewsContext();
  const subLevelNumber = level?.length || 0;

  const getJsonArray = useCallback(
    (index: string) => {
      if (newItems) {
        return Array.isArray(newItems[parseInt(index, 10)]?.value)
          ? newItems[parseInt(index, 10)]?.value.map((value: any) => {
              return JSON.parse(value);
            })
          : null;
      }
      return null;
    },
    [newItems],
  );

  return (
    <RowItem
      key={row.id}
      ref={ref}
      data-index={dataIndex}
      rowHeight={rowHeight || 22}
      parentExpanded={parentExpanded}
      subLevelNumber={subLevelNumber}
      subgroupColor={subgroupColor}
      style={style}
    >
      {row?.getVisibleCells()?.map((cell) => {
        const {
          column: { id },
          row: { id: index },
        } = cell;

        const { minSize, minWidth, width } = cell?.column?.columnDef;

        const value = cell.getValue();

        const isRowExpandable = expandableRows?.rows.includes(
          cell.row.values[expandableRows?.key],
        );

        const hasModalInfo = () => {
          const subComponent = restItem[Number(index)]?.subComponent;

          if (subComponent) {
            return subComponent?.type === 'modal_responsive_table_request';
          }
          return false;
        };

        const findActiveTab = Array.from(activeTabs?.values())?.find((item) =>
          item?.selected.find((selected) => selected?.for_column === id),
        );

        const valueForColumn = findActiveTab?.selected.find(
          (tab) => tab.for_column === id.replace(/_placeholder(_\d+)?$/, ''),
        );

        const teste =
          valueForColumn?.for_column === id.replace(/_placeholder(_\d+)?$/, '');

        if (
          hasTabs &&
          !activeTabs.get(id.replace(/_placeholder(_\d+)?$/, ''))?.hasValue &&
          !teste &&
          tabsViewsMap.get(id.replace(/_placeholder(_\d+)?$/, ''))
        ) {
          return <React.Fragment key={id} />;
        }

        if (id === 'expander' || !restItem[parseInt(index, 10)][id]) {
          return (
            <CustomExpander
              expandableRows={expandableRows}
              expanderBackgroundColor={expanderBackgroundColor}
              isRowExpandable={isRowExpandable}
              width={width}
              minWidth={minWidth}
              onClick={() => {
                closeRows();
                row.toggleExpanded(!row.getIsExpanded());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  closeRows();
                  row.toggleExpanded(!row.getIsExpanded());
                }
              }}
              row={row}
            />
          );
        }
        const { ...rest } = restItem[parseInt(index, 10)][id];
        const jsonArray = getJsonArray(index);
        const keysValue = jsonArray
          ? jsonArray?.find((jsonElement: any) => {
              return jsonElement.date === id;
            })
          : null;

        const exportFilter = (): string => {
          if (newItems) {
            return newItems[index]?.export_filter ?? '';
          }
          return '';
        };

        const exportFilterToPayload = () => {
          const parsedFilters = JSON.parse(exportFilter());
          const pieces = Object.keys(parsedFilters).map((key) => {
            const parsedKey = `[${key.replace(/\./g, '][')}]`;
            return `&filter${parsedKey}=${parsedFilters[key]}`;
          });
          return pieces.join('');
        };
        const exportWithLineFilter = () =>
          onExport ? onExport(exportFilterToPayload()) : null;

        const activeAccordion =
          tabsViews?.find((tab) => tab.column === id)?.hierarchyOrder ??
          Infinity;

        return (
          <CellContainer
            key={id}
            cellPadding={cellPadding || 2}
            cellBorderColor={cellBorderColor}
            minWidth={minWidth || minSize}
          >
            <Cell
              heatColorStart={heatColorStart}
              heatColorEnd={heatColorEnd}
              {...rest}
              rowHeight={rowHeight}
              value={value as string}
              keysValue={keysValue ?? null}
              dashMenuOptions={dashMenuOptions}
              justifyBadge={justifyBadge}
              textMargin={textMargin}
              onExport={exportWithLineFilter}
              deltaReversed={deltaReversed}
              activeAccordion={Array.from(activeTabs?.values())
                ?.map((tab) => tab?.hierarchyOrder)
                ?.some((el) => el > activeAccordion)}
              modalSubComponent={
                hasModalInfo() &&
                id === restItem[Number(index)].subComponent?.modal_trigger
                  ? {
                      dataClicked: {
                        ...cell,
                        indexValue: row?.original[contentInfo.indexBy],
                        id: cell?.getValue(),
                        value: cell?.getValue(),
                      },
                      dataSubcomponent: {
                        ...restItem[Number(index)].subComponent,
                        title: value,
                      },
                      dataContent: contentInfo,
                    }
                  : null
              }
              cellClicked={{
                ...cell,
                ...rest,
                value: cell?.getValue(),
                indexValue: row?.original?.[contentInfo?.indexBy],
                dataSubcomponent: {
                  ...restItem[Number(index)].subComponent,
                  title: value,
                },
                row,
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Cell>
          </CellContainer>
        );
      })}
    </RowItem>
  );
};

export default Row;
